import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { AccountingReport, AccountingShare } from '../components/AccountingReport';
import { api, ApiError, files } from '../lib/api';
import { amount } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/finance.css';

const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);

interface Row {
  id: string; type: string; typeLabel: string; number: string; title: string | null;
  status: string; statusLabel: string; issueDate: number | null;
  counterparty: string | null; counterpartyId: string | null;
  contractId: string | null; contract: { number: string; title: string | null } | null;
  currency: string; totalMinor: number; paidMinor: number; outstandingMinor: number;
}
interface Totals {
  count: number; billedMinor: number; collectedMinor: number; pendingMinor: number;
  purchasedMinor: number; paidOutMinor: number; payableMinor: number;
  marginMinor: number; executedMinor: number;
}
interface Ledger { totals: Totals; rows: Row[] }
interface ContractOpt { id: string; number: string; title: string | null; counterparty: string | null }
interface CpOpt { id: string; name: string }

const SIDES: Array<{ code: string; label: string }> = [
  { code: 'all', label: 'Todo' },
  { code: 'sales', label: 'Ventas' },
  { code: 'purchases', label: 'Compras' },
  { code: 'cash', label: 'Cobros y pagos' },
];

const toEpoch = (v: string): number | null => (v ? new Date(`${v}T00:00:00`).getTime() : null);

export function Accounting() {
  const { company } = useSession();
  const nav = useNavigate();

  const [side, setSide] = useState('all');
  const [contractId, setContractId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [unassigned, setUnassigned] = useState(false);

  const [data, setData] = useState<Ledger | null>(null);
  const [contracts, setContracts] = useState<ContractOpt[]>([]);
  const [cps, setCps] = useState<CpOpt[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);
  const [sharing, setSharing] = useState(false);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    if (side !== 'all') p.set('side', side);
    if (contractId) p.set('contractId', contractId);
    if (counterpartyId) p.set('counterpartyId', counterpartyId);
    const f = toEpoch(from); const t = toEpoch(to);
    if (f) p.set('from', String(f));
    if (t) p.set('to', String(t + 86_400_000 - 1));
    if (unassigned) p.set('unassigned', 'true');
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [side, contractId, counterpartyId, from, to, unassigned]);

  const load = useCallback(() => {
    if (!company) return;
    setData(null); setErr(null);
    api.get<Ledger>(`/accounting/ledger${qs()}`).then(setData).catch((e) => setErr(e instanceof ApiError ? e.message : 'No se pudo cargar.'));
  }, [company, qs]);

  useEffect(load, [load]);
  useEffect(() => {
    if (!company) return;
    api.get<ContractOpt[]>('/accounting/contracts').then(setContracts).catch(() => void 0);
    api.get<CpOpt[]>('/counterparties').then(setCps).catch(() => void 0);
  }, [company]);

  const cur = company?.currency ?? 'AED';
  const chosenContract = contracts.find((c) => c.id === contractId);

  /** Hoja de cálculo generada en el propio navegador: funciona siempre. */
  const csvLocal = () => {
    if (!data) return;
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['Fecha', 'Tipo', 'Number', 'Item', 'Contraparte', 'Contrato', 'Estado', 'Moneda', 'Total', 'Pendiente'];
    const lines = [head.map(esc).join(';')];
    for (const r of data.rows) {
      lines.push([
        r.issueDate ? new Date(r.issueDate).toLocaleDateString('es-ES') : '',
        r.typeLabel, r.number, r.title ?? '', r.counterparty ?? '',
        r.contract?.number ?? 'Generales', r.statusLabel, r.currency,
        (r.totalMinor / 100).toFixed(2).replace('.', ','),
        (r.outstandingMinor / 100).toFixed(2).replace('.', ','),
      ].map(esc).join(';'));
    }
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'contabilidad.csv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const exportar = async (kind: 'pdf' | 'csv') => {
    if (kind === 'csv') {
      if (PREVIEW) { csvLocal(); return; }
      setBusy('csv');
      try { await files.download(`/accounting/ledger.csv${qs()}`, 'contabilidad.csv'); }
      catch { csvLocal(); }
      finally { setBusy(null); }
      return;
    }
    // PDF: en local lo genera el servidor; en la vista previa se imprime el informe.
    if (PREVIEW) { setViewing(true); setTimeout(() => window.print(), 400); return; }
    setBusy('pdf');
    try { await files.download(`/accounting/ledger.pdf${qs()}`, 'contabilidad.pdf'); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not export. You can open "View report" and save it from there.'); }
    finally { setBusy(null); }
  };

  const resumen = (): string => {
    if (!data) return '';
    const t = data.totals;
    const alcance = chosenContract ? `contrato ${chosenContract.number}` : unassigned ? 'gastos generales' : 'todos los movimientos';
    const periodo = [from ? `desde ${from}` : '', to ? `hasta ${to}` : ''].filter(Boolean).join(' ') || 'full history';
    const texto = [
      `Resumen de contabilidad — ${company?.tradeName ?? ''}`,
      `Alcance: ${alcance} · ${periodo}`,
      `Facturado: ${amount(t.billedMinor, cur)}`,
      `Cobrado: ${amount(t.collectedMinor, cur)}`,
      `Pendiente de cobro: ${amount(t.pendingMinor, cur)}`,
      `Compras y gastos: ${amount(t.purchasedMinor, cur)}`,
      `Resultado: ${amount(t.marginMinor, cur)}`,
      `${t.count} movimientos.`,
    ].join('\n');
    return texto;
  };

  const limpiar = () => { setSide('all'); setContractId(''); setCounterpartyId(''); setFrom(''); setTo(''); setUnassigned(false); };

  return (
    <main className="main main--wide">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">Finanzas</span>
            <h1 className="h1">Contabilidad</h1>
            <p className="page-head__sub">Filter by contract, period or counterparty and see what has been issued, collected and what remains open. Exportable and printable.</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="acc-filters">
        <div className="acc-seg">
          {SIDES.map((s) => (
            <button key={s.code} className={side === s.code ? 'is-on' : ''} onClick={() => setSide(s.code)}>{s.label}</button>
          ))}
        </div>

        <div className="field" style={{ minWidth: 210 }}>
          <label>Contrato o proyecto</label>
          <select className="select" value={contractId} onChange={(e) => { setContractId(e.target.value); if (e.target.value) setUnassigned(false); }}>
            <option value="">Todos</option>
            {contracts.map((c) => <option key={c.id} value={c.id}>{c.number}{c.counterparty ? ` · ${c.counterparty}` : ''}</option>)}
          </select>
        </div>

        <div className="field" style={{ minWidth: 190 }}>
          <label>Cliente o proveedor</label>
          <select className="select" value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)}>
            <option value="">Todos</option>
            {cps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="field" style={{ width: 150 }}><label>Desde</label><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="field" style={{ width: 150 }}><label>Hasta</label><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>

        <label className="row gap-8 center" style={{ fontSize: 13, cursor: 'pointer', paddingBottom: 8 }}>
          <input type="checkbox" checked={unassigned} onChange={(e) => { setUnassigned(e.target.checked); if (e.target.checked) setContractId(''); }} />
          Solo gastos generales
        </label>

        <button className="btn btn--ghost btn--sm" onClick={limpiar} style={{ marginBottom: 2 }}>Limpiar</button>
      </div>

      {err && <div className="act-error" style={{ marginBottom: 16 }}><Icon name="alert" size={16} />{err}</div>}

      {/* Totales */}
      {data && (
        <div className="acc-totals">
          <div className="acc-total"><div className="acc-total__k">Facturado</div><div className="acc-total__v">{amount(data.totals.billedMinor, cur)}</div></div>
          <div className="acc-total"><div className="acc-total__k">Cobrado</div><div className="acc-total__v acc-total__v--good">{amount(data.totals.collectedMinor, cur)}</div></div>
          <div className="acc-total"><div className="acc-total__k">Pendiente de cobro</div><div className="acc-total__v acc-total__v--bad">{amount(data.totals.pendingMinor, cur)}</div></div>
          <div className="acc-total"><div className="acc-total__k">Compras y gastos</div><div className="acc-total__v">{amount(data.totals.purchasedMinor, cur)}</div></div>
          <div className="acc-total"><div className="acc-total__k">Resultado</div><div className={`acc-total__v ${data.totals.marginMinor >= 0 ? 'acc-total__v--good' : 'acc-total__v--bad'}`}>{amount(data.totals.marginMinor, cur)}</div></div>
        </div>
      )}

      {/* Acciones */}
      <div className="row gap-10 wrap" style={{ marginBottom: 16 }}>
        <button className="btn btn--ghost btn--sm" onClick={() => setViewing(true)} disabled={!data || data.rows.length === 0}>
          <Icon name="file" size={15} />Ver informe
        </button>
        <button className="btn btn--ghost btn--sm" onClick={() => exportar('pdf')} disabled={busy === 'pdf'}>
          <Icon name="download" size={15} />{busy === 'pdf' ? 'Generando…' : 'Descargar PDF'}
        </button>
        <button className="btn btn--ghost btn--sm" onClick={() => exportar('csv')} disabled={busy === 'csv'}>
          <Icon name="download" size={15} />{busy === 'csv' ? 'Generando…' : 'Descargar Excel'}
        </button>
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost btn--sm" onClick={() => setSharing(true)} disabled={!data}>
          <Icon name="share" size={15} />Compartir
        </button>
      </div>

      {/* Libro */}
      <div className="card">
        <div className="card__body">
          {!data && !err && <div className="faint" style={{ padding: 30 }}>Calculando…</div>}
          {data && data.rows.length === 0 && <div className="empty">No hay movimientos con estos filtros.</div>}
          {data && data.rows.length > 0 && (
            <table className="acc-table">
              <thead>
                <tr>
                  <th>Fecha</th><th>Tipo</th><th>Number</th><th>Contraparte</th><th>Imputado a</th><th>Estado</th>
                  <th className="r">Total</th><th className="r">Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} onClick={() => nav(`/documentos/${r.id}`)}>
                    <td className="num">{r.issueDate ? new Date(r.issueDate).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}</td>
                    <td>{r.typeLabel}</td>
                    <td className="num">{r.number}</td>
                    <td>{r.counterparty ?? '—'}</td>
                    <td>{r.contract ? <span className="acc-chip">{r.contract.number}</span> : <span className="acc-chip acc-chip--gen">Generales</span>}</td>
                    <td>{r.statusLabel}</td>
                    <td className="r num">{amount(r.totalMinor, r.currency)}</td>
                    <td className="r num" style={{ color: r.outstandingMinor > 0 ? 'var(--clay)' : 'var(--mist)' }}>{amount(r.outstandingMinor, r.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {viewing && data && (
        <AccountingReport
          companyName={company?.tradeName ?? ''}
          scope={chosenContract ? `Contrato ${chosenContract.number}` : unassigned ? 'Gastos generales' : 'Todos los contratos'}
          period={from || to ? `${from ? `desde ${from}` : ''} ${to ? `hasta ${to}` : ''}`.trim() : 'full history'}
          totals={data.totals}
          rows={data.rows}
          currency={cur}
          onClose={() => setViewing(false)}
          onServerPdf={PREVIEW ? undefined : () => exportar('pdf')}
        />
      )}

      {sharing && data && <AccountingShare text={resumen()} onClose={() => setSharing(false)} />}
    </main>
  );
}
